import { Button, Flex } from '@brightdrop/bd-ui';
import { TableModal } from '@brightdrop/table-ui';
import { type ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BatchSummaryVehicleBoardingSession } from '~/api/generated-api';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { useLocalizedIntegerCount } from '~/common/localization/useLocalizedFormatters';
import { RouteName } from '~/common/models/route.model';
import { findRoute, makeRoutePath } from '~/common/utils/route/route.utils';
import { useFailedVehiclesDataController } from './hooks/useFailedVehiclesDataController';
import { getFailedVehicleListColumns } from './list/config/FailedVehicleList.config';

type FailedVehiclesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationsId: string;
  batchId: string;
};

/**
 * Modal displaying the list of failed vehicles from a batch upload.
 * Uses TableModal with read-only table, search, and pagination.
 */
export const FailedVehiclesModal = ({
  isOpen,
  onClose,
  organizationsId,
  batchId,
}: FailedVehiclesModalProps): JSX.Element => {
  const { dataController, searchBarConfig } = useFailedVehiclesDataController(
    organizationsId,
    batchId,
    isOpen
  );
  const columns = useMemo(() => getFailedVehicleListColumns(), []);
  const formatCount = useLocalizedIntegerCount();
  const totalCount = dataController.totalCount;

  const navigate = useNavigate();
  // Dynamically construct the batch summary details link
  const batchSummaryDetailsLink = useMemo(() => {
    const route = findRoute(RouteName.ORGANIZATIONS_VEHICLES_BATCH);
    const link = makeRoutePath(route, {
      organizationsId,
      batchId,
    });

    return link;
  }, [batchId, organizationsId]);

  const customActionsContent: ReactNode = useMemo(() => {
    return (
      <Flex style={{ justifyContent: 'space-between' }}>
        <Button
          variant="tertiary"
          onClick={() => {
            onClose();
            navigate(batchSummaryDetailsLink);
          }}
          data-testid="failed-vehicles-modal-all-results-button"
        >
          {translateText('asset:failedVehicles.allResults')}
        </Button>
        <Button
          onClick={onClose}
          data-testid="failed-vehicles-modal-close-button"
        >
          {translateText('common:close')}
        </Button>
      </Flex>
    );
  }, [onClose, batchSummaryDetailsLink, navigate]);

  const toolbarConfig = useMemo(
    () => ({
      countLabelConfig: {
        translationKey: 'asset:failedVehicles.failedVehicleList.count',
        variables: {
          count: totalCount,
          formattedCount: formatCount(totalCount),
        },
      },
      searchBarConfig,
    }),
    [formatCount, searchBarConfig, totalCount]
  );

  return (
    <TableModal<BatchSummaryVehicleBoardingSession>
      isOpen={isOpen}
      onClose={onClose}
      title={translateText('asset:failedVehicles.title')}
      buttonsConfig={[]}
      customActionsContent={customActionsContent}
      columns={columns}
      dataController={dataController}
      toolbarConfig={toolbarConfig}
      hideBanner={true}
      readMode={true}
      shrinkToFit={true}
    />
  );
};
